import { AfterViewInit, Component, Directive, EventEmitter, Host, Injectable, Input, OnChanges, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { DropPosition, TreeItemLookup, DropAction, TreeItemDropEvent, LoadMoreRequestArgs, EditService, TreeItemAddRemoveArgs, ItemLookup } from '@progress/kendo-angular-treeview';
import { Style } from '@app/core/services/core-component.service';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { KendoBaseComponent } from '../kendo-base/kendo-base.component';
import { RememberService } from '@app/core/services/remember.service';
import { BehaviorSubject, debounceTime, filter, from, map, Observable, of, Subject, Subscription, take, tap } from 'rxjs';
import { TreeViewComponent as KendoTreeViewComponent } from '@progress/kendo-angular-treeview';
import { ChildMailFoldersParams, GraphMailService, RootMailFoldersParams } from '@app/modules/microsoft-graph/services/graph-mail.service';
import { MailFolder } from '@microsoft/microsoft-graph-types';
@Component({
    selector: 'tt-tree-view',
    templateUrl: './tree-view.component.html',
    styleUrls: ['./tree-view.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class TreeViewComponent<TType = { [key: string]: any }> extends KendoBaseComponent implements OnChanges, AfterViewInit {
    @ViewChildren(KendoTreeViewComponent)
    treeView?: QueryList<KendoTreeViewComponent>;

    /**
     * The is to store the last selected id(s) in 616 with. A list or single string depending on `ttSelectable` prop.
     */
    @Input()
    public ttRememberId: string = '';

    /**
     * Milliseconds between the storing of each change if ttRememberId is provided.
     *
     * @default 500
     */
    @Input()
    public set ttRememberDebounceTime(value: NumberInput) {
        this._rememberDebounceTime = coerceNumberProperty(value);
    }
    public get ttRememberDebounceTime(): number {
        return this._rememberDebounceTime;
    }
    private _rememberDebounceTime: number = 500;

    /**
     * Whether the tree should not retrieve the last stored selected id upon initialization and only remember the new selected id.
     *
     * @default true
     */
    @Input()
    public set ttOnlyRemember(value: BooleanInput) {
        this._onlyRemember = coerceBooleanProperty(value);
    }
    public get ttOnlyRemember(): boolean {
        return this._onlyRemember;
    }
    private _onlyRemember = true;

    /**
     * The id to use for storing expanded ids of the list.
     * The value stored is a list of the id of the expanded items.
     */
    @Input()
    public ttRememberIdExpanded: string = '';

    /**
     * Milliseconds between the storing of each change if ttRememberIdExpanded is provided.
     *
     * @default 500
     */
    @Input()
    public set ttRememberExpandedDebounceTime(value: NumberInput) {
        this._rememberExpandedDebounceTime = coerceNumberProperty(value);
    }
    public get ttRememberExpandedDebounceTime(): number {
        return this._rememberExpandedDebounceTime;
    }
    private _rememberExpandedDebounceTime: number = 500;

    /**
     * Whether the tree should not retrieve the last stored selected id upon initialization and only remember the new selected id.
     *
     * @default true
     */
    @Input()
    public set ttOnlyRememberExpanded(value: BooleanInput) {
        this._onlyRememberExpanded = coerceBooleanProperty(value);
    }
    public get ttOnlyRememberExpanded(): boolean {
        return this._onlyRememberExpanded;
    }
    private _onlyRememberExpanded = true;

    // @Input()
    // public set ttRememberExpanded(value: BooleanInput) {
    //     this._rememberExpanded = coerceBooleanProperty(value);
    // }
    // public get ttRememberExpanded(): boolean {
    //     return this._rememberExpanded;
    // }
    // private _rememberExpanded = false;

    /**
     * **Only applicable when using `ttSelectable="single"` | `ttSelectable="true"`** \
     * The id of the currently selected node(s).
     */
    @Input()
    public ttSelectedId: string | string[] = '';

    /**
     * **Only applicable when using `ttSelectable="multiple"`** \
     * List of the currently selected ids.
     */
    @Input()
    public ttSelectedIds: string[] = [];

    /**
     * **Only applicable when using `ttSelectable="single"` | `ttSelectable="true"`** \
     * Event emitted when a new item in the tree has been selected.
     */
    @Output()
    public ttSelectedIdChange = new EventEmitter<string>();

    /**
     * **Only applicable when using `ttSelectable="multiple"`** \
     * Event emitted when w new item in the tree has been selected.
     */
    @Output()
    public ttSelectedIdsChange = new EventEmitter<string[]>();

    @Input()
    public set ttAutoSelectFirst(value: BooleanInput) {
        this._autoSelectFirst = coerceBooleanProperty(value);
    }
    public get ttAutoSelectFirst(): boolean {
        return this._autoSelectFirst;
    }
    private _autoSelectFirst: boolean = false;

    /**
     * Event emitted when an item has been selected. Returns list of all selected items.
     * If selectable setting is `true` or `'single'` the list will only contain one item.
     */
    @Output()
    public ttOnSelect = new EventEmitter<TType[]>();

    /**
     * The list of nodes to show in the tree-view.
     */
    @Input()
    public ttData: TType[] = [];
    public data$ = new BehaviorSubject<TType[]>([]);

    /**
     * Emits an event when there's changes to the `ttData` list (includes sorting, removing and adding of items). The changed list is passed as the event.
     */
    @Output()
    public ttDataChange = new EventEmitter<TType[]>();

    /**
     * Emits an event when a node has been dropped. Passes the dropped item with its new parent id in the event.
     */
    @Output()
    public ttDrop = new EventEmitter<TType>();

    @Input()
    public ttFilter: string = '';

    /**
     * Whether or not it should be possible to drag and drop nodes around in the tree-view. Default is `false`.
     */
    @Input()
    public get ttDraggable(): boolean {
        return this._draggable;
    }
    public set ttDraggable(value: BooleanInput) {
        this._draggable = coerceBooleanProperty(value);
    }
    private _draggable = false;

    /**
     * Whether it should be possible to select node/s from the tree-view or not. The default is `true`.
     * @type `boolean | 'single' | 'multiple'`
     * @default true
     */
    @Input()
    public get ttSelectable(): boolean | 'single' | 'multiple' {
        return this._selectable;
    }
    public set ttSelectable(value: 'single' | 'multiple' | BooleanInput) {
        if (value === 'single' || value === 'multiple') {
            this._selectable = true;
        } else {
            this._selectable = coerceBooleanProperty(value);
        }
    }
    private _selectable: boolean | 'single' | 'multiple' = true;

    /**
     * The key of the id property of the tree-view node data.
     *
     * @default 'item_id'
     */
    @Input()
    public set ttDataId(value: keyof TType) {
        this._dataId = value;
    }
    public get ttDataId(): keyof TType {
        return this._dataId;
    }
    private _dataId = 'item_id' as keyof TType;

    /**
     * The key of the parent id property of the tree-view node data.
     *
     * @default 'item_id_parent'
     */
    @Input()
    public set ttDataParentId(value: keyof TType) {
        this._parentId = value;
    }
    public get ttDataParentId(): keyof TType {
        return this._parentId;
    }
    private _parentId = 'item_id_parent' as keyof TType;

    /**
     * The key of the name property of the tree-view node data.
     *
     * @default 'item_name'
     */
    @Input()
    public set ttDataName(value: keyof TType) {
        this._nameId = value;
    }
    public get ttDataName(): keyof TType {
        return this._nameId;
    }
    private _nameId = 'item_name' as keyof TType;

    /**
     * The key of the icon property of the tree-view node data.
     *
     * @default 'item_glyphicon'
     */
    @Input()
    public set ttDataIcon(value: keyof TType) {
        this._iconId = value;
    }
    public get ttDataIcon(): keyof TType {
        return this._iconId;
    }
    private _iconId = 'item_glyphicon' as keyof TType;

    /**
     * The key of the icon-color property of the tree-view node data.
     *
     * @default 'item_glyphicon_color'
     */
    @Input()
    public set ttDataIconColor(value: keyof TType) {
        this._iconColorId = value;
    }
    public get ttDataIconColor(): keyof TType {
        return this._iconColorId;
    }
    private _iconColorId = 'item_glyphicon_color' as keyof TType;

    /**
     * The key of the nestable property of the tree-view node data.
     *
     * @default 'item_nestable'
     */
    @Input()
    public set ttDataNestable(value: keyof TType) {
        this._nestableId = value;
    }
    public get ttDataNestable(): keyof TType {
        return this._nestableId;
    }
    private _nestableId = 'item_nestable' as keyof TType;

    /**
     * The key of the expanded property of the tree-view node data.
     *
     * @default 'isexpanded'
     */
    @Input()
    public set ttDataExpanded(value: keyof TType) {
        this._expandedId = value;
    }
    public get ttDataExpanded(): keyof TType {
        return this._expandedId;
    }
    private _expandedId = 'isexpanded' as keyof TType;

    /**
     * The key of the orderby property of the tree-view node data. This property will be updated if the tree node is dragged to a new position.
     *
     * @default 'orderby'
     */
    @Input()
    public set ttDataOrderBy(value: keyof TType) {
        this._orderBy = value;
    }
    public get ttDataOrderBy(): keyof TType {
        return this._orderBy;
    }
    private _orderBy = 'orderby' as keyof TType;

    @Input()
    public set ttUseDataSourceDirective(value: BooleanInput) {
        this._useDataSourceDirective = coerceBooleanProperty(value);
    }
    public get ttUseDataSourceDirective(): boolean {
        return this._useDataSourceDirective;
    }
    private _useDataSourceDirective = false;

    /**
     * Inline styling of elements in the component.
     */
    style: Style = {
        node: {},
    };

    /**
     * List of ids of the nodes which are expanded.
     */
    expandedIds: string[] = [];

    /**
     * List of ids of the nodes which are selected.
     */
    selectedIds: (string | number)[] = [];

    public rememberExpandedSubject = new Subject<string[]>();

    private rememberExpandedSubscription?: Subscription;

    public rememberSubject = new Subject<unknown>();

    private rememberSubscription?: Subscription;

    constructor(private remember: RememberService) {
        super();
        this.data$.subscribe({
            next: (data) => {
                this.ttData = data;
            },
        });
    }

    protected rememberModel(value: unknown) {
        if (!!this.ttRememberId) {
            this.remember.remember(this.ttRememberId, value);
        }
    }

    protected rememberExpanded(value: string[]) {
        if (!!this.ttRememberIdExpanded) {
            this.remember.remember(this.ttRememberIdExpanded, this.base64UrlEncodeUnicode(JSON.stringify(value)));
            const getStore = this.getStorage();

            getStore[this.ttRememberIdExpanded] = value;

            localStorage.setItem('tt-treeview.expanded', JSON.stringify(getStore));
        }
    }

    private rememberSelected(value: string | string[]) {
        if (Array.isArray(value)) {
            this.rememberSubject.next(JSON.stringify(value));
        } else {
            this.rememberSubject.next(value);
        }
    }

    public onExpandedChange(event: string[]) {
        // console.dir(event);
        this.rememberExpandedSubject.next(event);
    }

    private getStorage(): { [key: string]: string[] } {
        let storage = {};

        try {
            storage = JSON.parse(localStorage.getItem('tt-treeview.expanded') ?? '');
        } catch (error) {
            storage = {};
        }

        return storage;
    }

    /**
     * Handles changes in the selected nodes in the tree-view. Emits the `ttModelChange` event passing the ids of the selected node/s in the event.
     *
     * @param event list of ids of the selected nodes.
     */
    onSelectedChange(event: string[]): void {
        if (event && event instanceof Array && this.ttSelectable === 'multiple') {
            this.ttSelectedIdsChange.emit(event);
            this.rememberSelected(event);
        } else if (event && event instanceof Array && (this.ttSelectable === 'single' || this.ttSelectable === true)) {
            this.ttSelectedIdChange.emit(event[0]);
            this.rememberSelected(event[0]);
        } else {
            this.ttSelectedIdChange.emit();
            this.rememberSelected('');
        }

        this.ttOnSelect.emit(event.map((id) => this.ttData.find((item) => item[this.ttDataId] === id)).filter((item) => !!item) as TType[]);
    }

    /**
     * Constructs the necessary classes to display the given font-awesome icon or glyphicon.
     * Returns a string containing the classes needed to be applied to display the icon.
     *
     * @param icon the icon to construct a string of classes for.
     * @returns the string of classes required to display the icon.
     */
    public getIconClasses(icon: string) {
        let classes = icon;
        if (!icon) return;
        if (icon.startsWith('fa-')) {
            classes = 'fa ' + icon;
        } else if (icon.startsWith('fab-')) {
            classes = 'fab ' + icon.slice(0, 2) + icon.slice(3);
        } else if (icon.startsWith('fad-')) {
            classes = 'fad ' + icon.slice(0, 2) + icon.slice(3);
        } else if (icon.startsWith('fal-')) {
            classes = 'fal ' + icon.slice(0, 2) + icon.slice(3);
        } else if (icon.startsWith('far-')) {
            classes = 'far ' + icon.slice(0, 2) + icon.slice(3);
        } else if (icon.startsWith('fas-')) {
            classes = 'fas ' + icon.slice(0, 2) + icon.slice(3);
        } else if (icon.startsWith('glyphicon ')) {
            classes = icon;
        } else {
            classes = 'glyphicon ' + icon + ' fa-fw';
        }

        return classes;
    }

    /**
     * Returns the icon reflecting the drag status for dropping the dragged element on the given destination-item.
     *
     * @param action the action of the drop.
     * @param destinationItem the destination item the drop action would apply to.
     * @returns the icon displaying the drag status icon.
     */
    public getDragStatusIcon(action: DropAction, destinationItem: TreeItemLookup): string {
        if ((destinationItem?.item?.dataItem && !this.isItemNestable(destinationItem?.item?.dataItem) && action === DropAction.Add) || (destinationItem?.parent && !this.isItemNestable(destinationItem.parent.item.dataItem) && action !== DropAction.Add)) {
            return 'k-i-cancel';
        }

        switch (action) {
            case DropAction.Add:
                return 'k-i-plus';
            case DropAction.InsertTop:
                return 'k-i-insert-top';
            case DropAction.InsertBottom:
                return 'k-i-insert-bottom';
            case DropAction.InsertMiddle:
                return 'k-i-insert-middle';
            case DropAction.Invalid:
            default:
                return 'k-i-cancel';
        }
    }

    /**
     * Handles drop event, sorts the ttData list and emits in in `ttDataChange`, and emits the `ttDrop` event with the node whose parent was changed passed in the event.
     *
     * @param event the kendo drop event.
     */
    public handleDrop(event: TreeItemDropEvent): void {
        console.log(event);
        if (!!this.ttLoadMoreItems) {
            event.setValid(true);
            return;
        }
        if ((this.isItemNestable(event.destinationItem.item.dataItem) && event.dropPosition === DropPosition.Over) || (event?.destinationItem?.parent && this.isItemNestable(event.destinationItem.parent.item.dataItem) && event.dropPosition !== DropPosition.Over)) {
            event.setValid(true);
            console.log(event);
            console.log(JSON.stringify(this.ttData, null, 2));

            if (event.dropPosition === DropPosition.After || event.dropPosition === DropPosition.Before) {
                const sourceItemIndex = this.ttData.findIndex((item) => item[this.ttDataId] === event.sourceItem.item.dataItem[this.ttDataId]);
                const destinationItemIndex = this.ttData.findIndex((item) => item[this.ttDataId] === event.destinationItem.item.dataItem[this.ttDataId]);

                if (sourceItemIndex !== -1) {
                    if (event.dropPosition === DropPosition.After) {
                        const orderby = +event.destinationItem.item.dataItem[this.ttDataOrderBy] + 1;

                        this.ttData[sourceItemIndex][this.ttDataOrderBy] = orderby as TType[keyof TType];
                        // update the orderbys by all consecutive items.
                        this.ttData.forEach((item) => {
                            if (+item[this.ttDataOrderBy] >= +orderby) {
                                item[this.ttDataOrderBy] = (+item[this.ttDataOrderBy] + 1) as TType[keyof TType];
                            }
                        });
                    } else if (event.dropPosition === DropPosition.Before) {
                        if (destinationItemIndex !== -1) {
                            const orderby = +event.destinationItem.item.dataItem[this.ttDataOrderBy];

                            this.ttData[sourceItemIndex][this.ttDataOrderBy] = orderby as TType[keyof TType];
                            this.ttData[destinationItemIndex][this.ttDataOrderBy] = (orderby + 1) as TType[keyof TType];
                            // update the orderbys by all consecutive items.
                            this.ttData.forEach((item) => {
                                if (+item[this.ttDataOrderBy] > +orderby) {
                                    item[this.ttDataOrderBy] = (+item[this.ttDataOrderBy] + 1) as TType[keyof TType];
                                }
                            });
                        }
                    }
                }
            }

            this.ttData = this.ttData.sort((a, b) => +a[this.ttDataOrderBy] - +b[this.ttDataOrderBy]);

            this.ttData.forEach((node, index) => (node[this.ttDataOrderBy] = (index + 1) as TType[keyof TType]));

            this.ttDataChange.emit(this.ttData);
            this.data$.next(this.ttData);
            this.ttDrop.emit(this.ttData.find((node) => node[this.ttDataId] === event.destinationItem.item.dataItem[this.ttDataId]));
        } else {
            event.setValid(false);
        }
    }

    /**
     * Returns `true` if the given item is nestable, `false` if not.
     *
     * @param item the item to check if is nestable.
     * @returns `true` if item is nestable, `false` if not.
     */
    private isItemNestable(item: TType): boolean {
        if (this.ttDataNestable) {
            return ['1', 1, 'true', true].includes(item[this.ttDataNestable] as any);
        }

        return false;
    }

    /**
     * Formats the given list of tree-view-nodes to fit the required structure of the kendo-tree-view component.
     *
     * @param data the list of tree-view nodes to format.
     * @param expandNodes whether to expand the nodes based on their expanded property.
     * @returns a list of tree-view-nodes which can be displayed in a kendo-tree-view component.
     */
    private formatData(data: TType[]): TType[] {
        return data.map(this.mapItemToTreeNode).filter((node) => !!node) as TType[];
    }

    /**
     * Maps the given item to a valid tree-node for kendo tree-view. If it could not be mapped to a valid tree-view, `null` is returned.
     *
     * @param item the item to map to a valid tree-node.
     * @returns a valid tree-node, or `null` if a valid tree-node could not be constructed.
     */
    private mapItemToTreeNode = (node: TType): TType | null => {
        if (!this.ttDataId) {
            throw Error('Id key for tree-view must be provided');
        }

        if (!node[this.ttDataId]) {
            console.error('Id missing for item, element is not included in tree >>: \n' + JSON.stringify(node, null, 2));
            return null;
        }

        if (!this.ttData.some((n) => n[this.ttDataId] === node[this.ttDataParentId])) {
            node[this.ttDataParentId] = '0' as TType[keyof TType];
        }

        if (!!this.ttDataParentId && node[this.ttDataParentId] <= '0') {
            delete node[this.ttDataParentId];
        }

        return node;
    };

    public trackBy = (_: number, item: any): string => {
        return item[this.ttDataId];
    };

    private getExpandedIdNodes(data: TType[]): string[] {
        const expandedNodeIds = data.filter((node) => [1, '1', 'true', true].includes(node?.[this.ttDataExpanded] as string | boolean)).map((node) => node[this.ttDataId]) as string[];
        return expandedNodeIds;
    }

    private setSelectedId(selectedId: string | number | string[]) {
        if (!!this.ttSelectable === true) {
            if (Array.isArray(selectedId) && selectedId.length > 0) {
                if (this.ttSelectable === 'single' || this.ttSelectable === true) {
                    this.selectedIds = ['' + selectedId[0]];
                } else if (this.ttSelectable === 'multiple') {
                    this.selectedIds = [...this.selectedIds].map((id) => '' + id);
                }
            } else if (!Array.isArray(selectedId)) {
                this.selectedIds = ['' + selectedId];
            } else {
                this.selectedIds = [];
            }
        } else {
            this.selectedIds = [];
        }
    }

    private async getLastModelState() {
        try {
            const value = (await this.remember.getLastStatus(this.ttRememberId))[0].variablevalue;
            const selectedId = JSON.parse(value);

            if ((!!selectedId && (typeof selectedId === 'string' || typeof selectedId === 'number')) || (Array.isArray(selectedId) && selectedId.every((id) => typeof id === 'string'))) {
                this.setSelectedId(selectedId);

                if (this.selectedIds.length > 0) {
                    if (this.ttSelectable === 'single' || this.ttSelectable === true) {
                        this.ttSelectedId = ('' + this.selectedIds[0]) as string;
                        this.ttSelectedIdChange.emit(this.ttSelectedId);
                    } else if (this.ttSelectable === 'multiple') {
                        this.ttSelectedIds = this.selectedIds as string[];
                        this.ttSelectedIdsChange.emit(this.ttSelectedIds);
                    }
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    public async getLastExpandedState() {
        try {
            //try and get from local storage first.
            const expandedIds = this.getStorage()[this.ttRememberIdExpanded];
            if (!!expandedIds && Array.isArray(expandedIds) && expandedIds.every((item) => typeof item === 'string')) {
                this.expandedIds = expandedIds;
            }
        } catch (error) {
            console.error(error);
        }
        try {
            const value = (await this.remember.getLastStatus(this.ttRememberIdExpanded))[0].variablevalue;

            const expandedIds = JSON.parse(this.base64UrlDecode(value));

            if (Array.isArray(expandedIds) && expandedIds.every((id) => typeof id === 'string')) {
                this.expandedIds = expandedIds;
            }
        } catch (error) {
            console.error(error);
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();

        if (!this.ttOnlyRemember && this.ttRememberId) {
            this.getLastModelState();
        }

        if (!this.ttOnlyRememberExpanded && this.ttRememberIdExpanded) {
            this.getLastExpandedState();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['ttUseDataSourceDirective']) {
            this.ttUseDataSourceDirective = changes['ttUseDataSourceDirective'].currentValue;
        }

        if (changes['ttPageSize']) {
            this.ttPageSize = changes['ttPageSize'].currentValue;
        }

        if (changes['ttTotalRootNodes']) {
            this.ttTotalRootNodes = changes['ttTotalRootNodes'].currentValue;
        }

        if (changes['ttOnlyRemember']) {
            this.ttOnlyRemember = changes['ttOnlyRemember'].currentValue;
        }

        if (changes['ttRememberDebounceTime']) {
            this.ttRememberDebounceTime = changes['ttRememberDebounceTime'].currentValue;
        }

        if (changes['ttRememberId'] || changes['ttRememberDebounceTime']) {
            this.rememberSubscription?.unsubscribe?.();

            if (this.ttRememberId) {
                this.rememberSubscription = this.rememberSubject.pipe(debounceTime(this.ttRememberDebounceTime)).subscribe({ next: (value) => this.rememberModel(value) });
            }
        }

        if (changes['ttOnlyRememberExpanded']) {
            this.ttOnlyRememberExpanded = changes['ttOnlyRememberExpanded'].currentValue;
        }

        if (changes['ttRememberExpandedDebounceTime']) {
            this.ttRememberExpandedDebounceTime = changes['ttRememberExpandedDebounceTime'].currentValue;
        }

        if (changes['ttRememberIdExpanded'] || changes['ttRememberExpandedDebounceTime']) {
            this.rememberExpandedSubscription?.unsubscribe?.();

            if (this.ttRememberIdExpanded) {
                this.rememberExpandedSubscription = this.rememberExpandedSubject.pipe(debounceTime(this.ttRememberExpandedDebounceTime)).subscribe({ next: (value) => this.rememberExpanded(value) });
            }
        }

        if (changes['ttAutoSelectFirst']) {
            this.ttAutoSelectFirst = changes['ttAutoSelectFirst'].currentValue;
        }

        if (changes['ttDataId']) {
            this.ttDataId = changes['ttDataId'].currentValue;
        }

        if (changes['ttDataName']) {
            this.ttDataName = changes['ttDataName'].currentValue;
        }

        if (changes['ttDataParentId']) {
            this.ttDataParentId = changes['ttDataParentId'].currentValue;
        }

        if (changes['ttDataExpanded']) {
            this.ttDataExpanded = changes['ttDataExpanded'].currentValue;
        }

        if (changes['ttDataNestable']) {
            this.ttDataNestable = changes['ttDataNestable'].currentValue;
        }

        if (changes['ttDataIcon']) {
            this.ttDataIcon = changes['ttDataIcon'].currentValue;
        }

        if (changes['ttDataIconColor']) {
            this.ttDataIconColor = changes['ttDataIconColor'].currentValue;
        }

        if (changes['ttDataOrderBy']) {
            this.ttDataOrderBy = changes['ttDataOrderBy'].currentValue;
        }

        if (changes?.['ttData']?.currentValue && changes['ttData'].currentValue !== changes['ttData'].previousValue) {
            this.ttData = this.formatData(changes['ttData'].currentValue);
            this.data$.next(this.ttData);

            if (changes['ttData'].firstChange) {
                this.expandedIds = this.getExpandedIdNodes(this.ttData);
            }

            this.calculatePageSize();
        }

        if (changes?.['ttDraggable']) {
            this.ttDraggable = changes['ttDraggable'].currentValue;
        }

        if (changes?.['ttSelectable']) {
            this.ttSelectable = changes['ttSelectable'].currentValue;
        }

        if (changes?.['ttSelectedId']?.currentValue) {
            this.setSelectedId(changes?.['ttSelectedId']?.currentValue);
        }
    }

    public pageSize = 999999;

    private calculatePageSize() {
        this.pageSize = this.ttData.length > 300 ? 5 : this.ttData.length > 120 ? 10 : this.ttData.length > 60 ? 20 : 999999;
    }

    @Input()
    public set ttPageSize(value: NumberInput) {
        this._pageSize = coerceNumberProperty(value);
    }
    public get ttPageSize(): number {
        return this._pageSize;
    }
    private _pageSize = 25;

    @Input()
    public set ttTotalRootNodes(value: NumberInput) {
        this._totalRootNodes = coerceNumberProperty(value);
    }
    public get ttTotalRootNodes(): number {
        return this._totalRootNodes;
    }
    private _totalRootNodes = 0;

    @Input()
    public ttDataTotalField: keyof TType = 'item_child_count' as keyof TType;

    @Input()
    public ttLoadMoreItems: ((args: LoadMoreRequestArgs) => Observable<TType[]>) | string = '';

    @Input()
    public ttHasChildren?: (item: TType) => boolean;

    @Input()
    public ttChildren?: (item: TType) => Observable<TType[]>;

    ngAfterViewInit(): void {
        if (this.ttData?.length > 0 && !!this.ttDataId && this.ttSelectable !== false && !this.ttSelectedId && this.ttAutoSelectFirst) {
            let defaultSelected = this.ttData[0][this.ttDataId];

            if (!!defaultSelected) {
                if (this.ttSelectable === 'multiple') {
                    this.ttSelectedIds = [defaultSelected] as string[];
                    this.selectedIds = this.ttSelectedIds;
                    this.ttSelectedIdsChange.emit(this.ttSelectedIds);
                    this.rememberSelected(this.ttSelectedIds);
                } else {
                    this.ttSelectedId = defaultSelected as string;
                    this.selectedIds = [this.ttSelectedId];
                    this.ttSelectedIdChange.emit(this.ttSelectedId);
                    this.rememberSelected(this.ttSelectedId);
                }
            }
        }

        setTimeout(() => {
            if (!!this.treeView && this.treeView?.length > 0) {
                this.onTreeViewLoaded$.next();
            }

            console.log(this.treeView);
            this.treeView?.changes.subscribe({ next: () => this.onTreeViewLoaded$.next() });
        });
    }

    onTreeViewLoaded$ = new Subject<void>();
}

export abstract class TreeDataSourceDirectiveBase<TType extends { [key: string]: any }> {
    public treeview: TreeViewComponent;

    public kendoTreeView?: KendoTreeViewComponent;

    public data: TType[] = [];

    constructor(@Host() treeview: TreeViewComponent) {
        this.treeview = treeview;
        treeview.onTreeViewLoaded$.subscribe({ next: () => this.init() });
    }

    public async init() {
        if (!this.treeview.treeView?.get(0)) {
            throw new Error('Use graphMailFoldersDataSource on <kendo-treeview>.');
        }

        this.kendoTreeView = this.treeview.treeView.get(0)!;

        if (this.treeview.ttRememberIdExpanded) {
            this.treeview.getLastExpandedState();
        }

        this.kendoTreeView.nodes = [];
        this.data = [];

        this.kendoTreeView.children = this.fetchChildren.bind(this) as any;
        this.kendoTreeView.hasChildren = this.hasChildren.bind(this) as any;
        this.kendoTreeView.textField = this.treeview.ttDataName as string;
        this.kendoTreeView.nodeDrop.subscribe(this.handleDrop.bind(this));

        const rootNodes = this.fetchRootNodes();

        if (rootNodes instanceof Promise) {
            const items = await rootNodes;
            this.setRootNodes(items);
        } else if (rootNodes instanceof Observable) {
            rootNodes.subscribe({ next: this.setRootNodes });
        }
    }

    private setRootNodes = (items: TType[]) => {
        if (!this.kendoTreeView) throw Error('Kendo tree view missing.');
        this.data = items;
        this.kendoTreeView.nodes = this.data.slice();
        this.treeview.data$.next(this.data.slice());
    };

    abstract fetchRootNodes(): Promise<TType[]> | Observable<TType[]>;

    abstract fetchChildren(item: TType): Observable<TType[]>;

    abstract hasChildren(item: TType): boolean;

    abstract handleDrop(args: TreeItemAddRemoveArgs): unknown;
}
